import React, { useState, useEffect, lazy, Suspense, startTransition } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MapPin, Bookmark, Users, User as UserIcon, LogOut, BookOpen, Cigarette, BarChart3, Globe, Sparkles, Image, Settings as SettingsIcon, Home } from 'lucide-react';

// Lazy Load Heavy Components
// Lazy Load Heavy Components
const MapFinder = lazy(() => import('./components/MapFinder').then(module => ({ default: module.MapFinder })));
const Sommelier = lazy(() => import('./components/Sommelier').then(module => ({ default: module.Sommelier })));
const CommunityHub = lazy(() => import('./components/CommunityHub').then(module => ({ default: module.CommunityHub })));
const PhotoGallery = lazy(() => import('./components/PhotoGallery').then(module => ({ default: module.PhotoGallery })));
const FlavorProfile = lazy(() => import('./components/FlavorProfile').then(module => ({ default: module.FlavorProfile })));
const Settings = lazy(() => import('./components/Settings').then(module => ({ default: module.Settings })));
const JourneyMap = lazy(() => import('./components/JourneyMap').then(module => ({ default: module.JourneyMap })));
const TermsOfService = lazy(() => import('./components/TermsOfService').then(module => ({ default: module.TermsOfService })));
const Analytics = lazy(() => import('./components/Analytics').then(module => ({ default: module.Analytics })));
const Humidor = lazy(() => import('./components/Humidor').then(module => ({ default: module.Humidor })));
const Wishlist = lazy(() => import('./components/Wishlist').then(module => ({ default: module.Wishlist })));
const CigarLogger = lazy(() => import('./components/CigarLogger').then(module => ({ default: module.CigarLogger })));
const YourNextStick = lazy(() => import('./components/YourNextStick').then(module => ({ default: module.YourNextStick })));

// Eager load core components needed for LCP
import { Dashboard } from './components/Dashboard';
import { Auth } from './components/Auth';
import { SubscriptionModal } from './components/SubscriptionModal';
import { AgeGate } from './components/AgeGate';
// Analytics was previously here, moved to lazy
import { Profile } from './components/Profile';
import { NotificationBell, NotificationPanel } from './components/NotificationPanel';
import { OfflineIndicator } from './components/OfflineIndicator';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import { useOfflineSync } from './hooks/useOfflineSync';
import { AppView, User, CigarLog, HumidorItem, WishlistItem } from './types';
import { auth, db } from './lib/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, setDoc, collection, query, where, deleteDoc, updateDoc, deleteField } from 'firebase/firestore';

import logo from './assets/logo.jpg';
import { InstallPrompt } from './components/InstallPrompt';

// ... imports

import { MoreMenu } from './components/MoreMenu';
import { Menu as MenuIcon } from 'lucide-react';
import { isAndroid } from './utils/platformUtils';
import { toDateMs } from './utils/timeUtils';
import { safeStorageGet, safeStorageRemove, safeStorageSet } from './utils/safeStorage';

const Nav: React.FC<{
  currentView: AppView,
  setView: (v: AppView) => void,
  user: User | null,
  onLogout: () => void,
  onNotificationClick: () => void,
  showNotifications: boolean
}> = ({ currentView, setView, user, onLogout, onNotificationClick, showNotifications }) => {
  const { t } = useLanguage();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const communityEnabled = !isAndroid();
  // NotificationPanel is used within Nav component

  const desktopNavItems = [
    { view: AppView.LOCATOR, icon: MapPin, label: t('nav.locate') },
    { view: AppView.HUMIDOR, icon: Cigarette, label: t('nav.humidor') },
    { view: AppView.LOG_SMOKE, icon: BookOpen, label: t('nav.log') },
    { view: AppView.WISHLIST, icon: Bookmark, label: t('nav.wishlist') },
    { view: AppView.ANALYTICS, icon: BarChart3, label: t('nav.analytics') },
    { view: AppView.JOURNEY, icon: Globe, label: t('nav.journey') },
    { view: AppView.GALLERY, icon: Image, label: t('nav.gallery') },
    { view: AppView.SOMMELIER, icon: Sparkles, label: t('nav.sommelier') },
    ...(communityEnabled ? [{ view: AppView.COMMUNITY, icon: Users, label: t('nav.community') }] : []),
    { view: AppView.PROFILE, icon: UserIcon, label: t('nav.profile') },
  ];

  return (
    <>
      <MoreMenu
        isOpen={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
        onNavigate={setView}
        onLogout={onLogout}
        currentView={currentView}
      />

      {/* Desktop Sidebar */}
      <nav className="hidden md:flex flex-col w-64 bg-brand-900 border-r border-brand-800 h-full fixed left-0 top-0 z-50 shadow-xl">
        <div className="p-6 flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setView(AppView.HOME)}>
          <img src={logo} alt="Aficionado Logo" className="w-10 h-10 object-contain drop-shadow-md rounded-full" />
          <div className="flex flex-col">
            <span className="text-lg font-serif font-bold text-brand-50 tracking-wide leading-none">Aficionado</span>
            <span className="text-[9px] text-gold-500 uppercase tracking-wider font-medium mt-0.5">Cigar Journal by Jamsoft</span>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {user && desktopNavItems.map(item => (
            <button
              key={item.label}
              onClick={() => setView(item.view)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all group ${currentView === item.view ? 'bg-brand-50 text-gold-600 shadow-sm border border-brand-800' : 'text-brand-500 hover:bg-brand-800 hover:text-brand-900'}`}
            >
              <item.icon size={22} className={`transition-transform group-hover:scale-110 ${currentView === item.view ? 'text-gold-600' : 'text-brand-400 group-hover:text-gold-500'}`} />
              <span className="font-medium text-lg">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-brand-800 space-y-2">
          {/* Notification & Settings Row */}
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <NotificationBell onClick={onNotificationClick} />
              <NotificationPanel isOpen={showNotifications} onClose={onNotificationClick} />
            </div>
            <button
              onClick={() => setView(AppView.SETTINGS)}
              className={`p-2 rounded-lg transition-colors ${currentView === AppView.SETTINGS ? 'text-gold-500 bg-brand-800' : 'text-brand-400 hover:text-gold-500'}`}
            >
              <SettingsIcon size={20} />
            </button>
          </div>

          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 p-3 text-brand-400 hover:text-red-400 hover:bg-brand-900/50 rounded-lg transition-colors text-sm"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </nav>

      {/* Mobile Bottom Navigation (Fixed 5 items) */}
      <nav
        className="md:hidden fixed left-4 right-4 bg-brand-900/90 backdrop-blur-xl border border-brand-700/50 rounded-2xl shadow-2xl z-50"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      >
        <div className="grid grid-cols-5 h-full px-1">
          {/* 1. Home */}
          <button
            onClick={() => setView(AppView.HOME)}
            className={`relative flex flex-col items-center justify-center gap-1 h-16 transition-all ${currentView === AppView.HOME ? 'text-gold-500' : 'text-brand-400'}`}
          >
            <Home size={22} className={`${currentView === AppView.HOME ? 'drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]' : ''}`} />
            <span className="text-[9px] font-medium">Home</span>
          </button>

          {/* 2. Humidor */}
          <button
            onClick={() => setView(AppView.HUMIDOR)}
            className={`relative flex flex-col items-center justify-center gap-1 h-16 transition-all ${currentView === AppView.HUMIDOR ? 'text-gold-500' : 'text-brand-400'}`}
          >
            <Cigarette size={22} className={`${currentView === AppView.HUMIDOR ? 'drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]' : ''}`} />
            <span className="text-[9px] font-medium">Humidor</span>
          </button>

          {/* 3. Log (Center Action) */}
          <button
            onClick={() => setView(AppView.LOG_SMOKE)}
            className="relative flex flex-col items-center justify-start -top-6"
          >
            <div className={`p-4 rounded-full shadow-lg border-4 border-brand-950 transition-all active:scale-95 ${currentView === AppView.LOG_SMOKE
              ? 'bg-gold-500 text-black shadow-gold-500/30 font-bold'
              : 'bg-brand-800 text-gold-500 hover:bg-brand-700'
              }`}>
              <BookOpen size={24} strokeWidth={2.5} />
            </div>
          </button>

          {/* 4. Sommelier */}
          <button
            onClick={() => setView(AppView.SOMMELIER)}
            className={`relative flex flex-col items-center justify-center gap-1 h-16 transition-all ${currentView === AppView.SOMMELIER ? 'text-gold-500' : 'text-brand-400'}`}
          >
            <Sparkles size={22} className={`${currentView === AppView.SOMMELIER ? 'drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]' : ''}`} />
            <span className="text-[9px] font-medium">AI</span>
          </button>

          {/* 5. More */}
          <button
            onClick={() => setShowMoreMenu(true)}
            className={`relative flex flex-col items-center justify-center gap-1 h-16 transition-all ${showMoreMenu ? 'text-gold-500' : 'text-brand-400'}`}
          >
            <MenuIcon size={22} />
            <span className="text-[9px] font-medium">More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

const AppContent: React.FC = () => {
  const [ageGateVisible, setAgeGateVisible] = useState<boolean>(true);
  const communityEnabled = !isAndroid();

  const [user, setUser] = useState<User | null>(null);

  // Separate state for subscription to avoid race conditions with user profile
  const [subscriptionStatus, setSubscriptionStatus] = useState<{ isSubscribed: boolean, renewalDate?: string } | null>(null);

  const [loadingUser, setLoadingUser] = useState(true);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const [currentView, setCurrentViewRaw] = useState<AppView>(AppView.HOME);

  // Wrapper to use startTransition for lazy-loaded component navigation
  const setCurrentView = (view: AppView) => {
    if (!communityEnabled) {
      if (view === AppView.COMMUNITY) {
        startTransition(() => setCurrentViewRaw(AppView.HOME));
        return;
      }
    }
    startTransition(() => {
      setCurrentViewRaw(view);
    });
  };
  const [cigarLogs, setCigarLogs] = useState<CigarLog[]>([]);
  const [humidorItems, setHumidorItems] = useState<HumidorItem[]>([]);
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [smokeContext, setSmokeContext] = useState<HumidorItem | null>(null);

  // New state for notifications panel
  const [showNotifications, setShowNotifications] = useState(false);

  // Offline sync hook
  const offlineSync = useOfflineSync(user?.uid);

  // Theme hook
  const { theme } = useTheme();
  useLanguage();
  // Note: 'language' is used by other components via context, but if we need it here we have it. 
  // If unused in App.tsx directly, linter warns. We can keep it if we plan to use it or remove. 
  // Since linter complains, I will just call useLanguage() without destructuring if I don't need 'language' variable, 
  // or consume it in logs. Actually I see I don't use 'language' in App.tsx JSX anymore.

  // Check if returning from payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_success') === 'true') {
      setVerifyingPayment(true);
      // Remove query param to clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Fallback: If subscription doesn't activate in 15s, stop verification
      const timer = setTimeout(() => setVerifyingPayment(false), 15000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const storedVerification = safeStorageGet('aficionado_age_verified');
    if (storedVerification === 'true') {
      setAgeGateVisible(false);
    }
  }, []);

  // --------------------------------------------------------
  // REAL-TIME AUTH & DATA SYNC
  // --------------------------------------------------------
  // --------------------------------------------------------
  // AUTHENTICATION LOGIC
  // --------------------------------------------------------
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // Just set the basic user info to trigger other effects
        setUser((prev) => ({
          ...(prev || { uid: firebaseUser.uid, email: firebaseUser.email || '', photoURL: firebaseUser.photoURL || '', displayName: firebaseUser.displayName || 'Aficionado' }),
          uid: firebaseUser.uid
        }));
      } else {
        setUser(null);
        setSubscriptionStatus(null);
        setCigarLogs([]);
        setHumidorItems([]);
        setWishlistItems([]);
        setLoadingUser(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // --------------------------------------------------------
  // DATA SYNC EFFECTS
  // --------------------------------------------------------

  // 1. Profile Sync
  useEffect(() => {
    if (!user?.uid) return;

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data() as User;
        setUser((prev) => prev ? { ...prev, ...userData } : userData);
      } else {
        // First time login - handled in Auth or here
        const newUser: User = {
          uid: user.uid,
          displayName: user.displayName || 'Aficionado',
          email: user.email || '',
          photoURL: user.photoURL || '',
          isSubscribed: false
        };
        setDoc(userRef, newUser, { merge: true });
      }
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // 2. Subscription Sync
  useEffect(() => {
    if (!user?.uid) return;

    const subsQuery = query(
      collection(db, 'customers', user.uid, 'subscriptions'),
      where('status', 'in', ['active', 'trialing'])
    );

    const unsubscribe = onSnapshot(subsQuery, (snapshot) => {
      const isSubscribed = !snapshot.empty;
      let renewalDate = '';

      if (!snapshot.empty) {
        const subData = snapshot.docs[0].data();
        if (subData.current_period_end) {
          renewalDate = new Date(subData.current_period_end.seconds * 1000).toLocaleDateString();
        }
      }

      setSubscriptionStatus({ isSubscribed, renewalDate });

      if (isSubscribed) {
        setVerifyingPayment(false);
      } else {
        // Cleanup community name if subscription is inactive.
        // Name ownership is represented directly on the user doc.
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, { communityName: deleteField() }).catch((error) => {
          console.error("Failed to clear community name after subscription loss:", error);
        });
      }
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // 3. Cigar Logs Sync
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(collection(db, 'cigar_logs'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logs: CigarLog[] = [];
      snapshot.forEach((doc) => {
        logs.push({ ...doc.data(), id: doc.id } as CigarLog);
      });
      setCigarLogs(
        logs.sort((a, b) => (toDateMs(b.date) ?? 0) - (toDateMs(a.date) ?? 0))
      );
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // 4. Wishlist Sync
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(collection(db, 'wishlist'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: WishlistItem[] = [];
      snapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id } as WishlistItem);
      });
      setWishlistItems(list);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // 5. Humidor Sync
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(collection(db, 'humidor'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: HumidorItem[] = [];
      snapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id } as HumidorItem);
      });
      setHumidorItems(list);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Loading State Logic
  useEffect(() => {
    if (user === null) {
      setLoadingUser(false);
    } else if (user && subscriptionStatus !== null) {
      setLoadingUser(false);
    }
  }, [user, subscriptionStatus]);

  // Merge User and Subscription State
  const effectiveUser = user ? {
    ...user,
    isSubscribed: subscriptionStatus?.isSubscribed ?? false,
    subscriptionRenewalDate: subscriptionStatus?.renewalDate
  } : null;

  const handleAgeVerify = (verified: boolean) => {
    if (verified) {
      safeStorageSet('aficionado_age_verified', 'true');
      setAgeGateVisible(false);
    } else {
      safeStorageRemove('aficionado_age_verified');
      setAgeGateVisible(true);
    }
  };

  const handleLogin = () => {
    setCurrentView(AppView.HOME);
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      await signOut(auth);
      setCurrentView(AppView.HOME);
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    if (effectiveUser) {
      const userRef = doc(db, 'users', effectiveUser.uid);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { isSubscribed, subscriptionRenewalDate, ...safeData } = updatedUser;
      await setDoc(userRef, safeData, { merge: true });
    }
  };

  const handleCancelSubscription = () => {
    alert("To cancel, please visit the Stripe Customer Portal.");
  };

  const handleSaveLog = async (newLog: CigarLog) => {
    if (effectiveUser) {
      const logRef = doc(collection(db, 'cigar_logs'));
      const logToSave = { ...newLog, id: logRef.id, userId: effectiveUser.uid };
      await setDoc(logRef, logToSave);
      setSmokeContext(null); // Clear smoke context after saving
    }
  };

  const handleDeleteLog = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      await deleteDoc(doc(db, 'cigar_logs', id));
    }
  };

  const handleUpdateLog = async (updatedLog: CigarLog) => {
    if (effectiveUser) {
      const logRef = doc(db, 'cigar_logs', updatedLog.id);
      // Always ensure userId is set to prevent orphaned documents
      const logToUpdate = { ...updatedLog, userId: effectiveUser.uid };
      await setDoc(logRef, logToUpdate, { merge: true });
    }
  };

  const handleSmokeFromHumidor = (item: HumidorItem) => {
    setSmokeContext(item);
    setCurrentView(AppView.LOG_SMOKE);
  };

  const renderContent = () => {
    return (
      <ErrorBoundary>
        <Suspense fallback={
          <div className="flex h-full items-center justify-center">
            <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        }>
          {(() => {
            switch (currentView) {
              case AppView.HOME:
                return <Dashboard
                  user={effectiveUser!}
                  logs={cigarLogs}
                  humidorItems={humidorItems}
                  onNavigate={setCurrentView}
                />;
              case AppView.SOMMELIER:
                return <Sommelier logs={cigarLogs} />;
              case AppView.LOCATOR:
                return <MapFinder />;
              case AppView.LOG_SMOKE:
                return <CigarLogger
                  logs={cigarLogs}
                  onLogSaved={handleSaveLog}
                  onLogUpdated={handleUpdateLog}
                  onDeleteLog={handleDeleteLog}
                  user={effectiveUser!}
                  initialCigar={smokeContext}
                />;
              case AppView.HUMIDOR:
                return effectiveUser ? <Humidor user={effectiveUser} items={humidorItems} onSmoke={handleSmokeFromHumidor} /> : null;
              case AppView.WISHLIST:
                return effectiveUser ? <Wishlist user={effectiveUser} items={wishlistItems} /> : null;
              case AppView.COMMUNITY:
                return !communityEnabled
                  ? <Dashboard
                      user={effectiveUser!}
                      logs={cigarLogs}
                      humidorItems={humidorItems}
                      onNavigate={setCurrentView}
                    />
                  : <CommunityHub currentUser={effectiveUser} onUpdateUser={handleUpdateUser} />;
              case AppView.ANALYTICS:
                return <Analytics logs={cigarLogs} />;
              case AppView.JOURNEY:
                return <JourneyMap logs={cigarLogs} onUpdateLog={handleUpdateLog} />;
              case AppView.GALLERY:
                return <PhotoGallery logs={cigarLogs} />;
              case AppView.FLAVOR_PROFILE:
                return <FlavorProfile logs={cigarLogs} />;
              case AppView.SETTINGS:
                return <Settings
                  isOnline={offlineSync.isOnline}
                  pendingSync={offlineSync.pendingCount}
                  onNavigate={setCurrentView}
                />;
              case AppView.TERMS:
                return <TermsOfService onBack={() => setCurrentView(AppView.SETTINGS)} />; // Default back to settings, but can vary
              case AppView.PROFILE:
              default:
                return <Profile
                  user={effectiveUser!}
                  logs={cigarLogs}
                  onCancelSubscription={handleCancelSubscription}
                  onLogout={handleLogout}
                  onUpdateUser={handleUpdateUser}
                />;
              case AppView.YOUR_NEXT_STICK:
                return <YourNextStick logs={cigarLogs} onNavigate={setCurrentView} />;
            }
          })()}
        </Suspense>
      </ErrorBoundary>
    );
  };

  // Initial Loading
  if (loadingUser && !ageGateVisible) {
    return (
      <div className="h-screen w-full bg-brand-950 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-gold-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  // Payment Verification Loading (Post-Redirect)
  if (verifyingPayment) {
    return (
      <div className="h-screen w-full bg-brand-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="animate-spin w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full mb-6"></div>
        <h2 className="text-2xl font-serif text-brand-50 mb-2">Verifying Membership</h2>
        <p className="text-brand-300">Please wait while we confirm your payment details...</p>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <NotificationProvider userId={effectiveUser?.uid}>
        <div className={`min-h-screen w-full font-sans selection:bg-gold-500/30 overflow-x-hidden transition-colors pt-safe pb-safe ${theme === 'light' ? 'bg-brand-50 text-brand-900' : 'bg-brand-950 text-brand-50'} `}>
          {ageGateVisible && <AgeGate onVerify={handleAgeVerify} />}

          {!ageGateVisible && !effectiveUser && (
            <div className="w-full h-screen flex flex-col relative z-40 bg-brand-950">
              {currentView === AppView.TERMS ? (
                <TermsOfService onBack={() => setCurrentView(AppView.HOME)} />
              ) : (
                <>
                  <Auth
                    onLogin={handleLogin}
                    onShowTerms={() => setCurrentView(AppView.TERMS)}
                  />
                  <div className="text-center p-4 text-brand-500 text-xs absolute bottom-0 w-full">
                    This application uses Google Gemini AI.
                  </div>
                </>
              )}
            </div>
          )}

          {/* SUBSCRIPTION GATING - Allow viewing terms/settings even if locked */}
          {!ageGateVisible && effectiveUser && !effectiveUser.isSubscribed && currentView !== AppView.TERMS && (
            <div className="w-full h-screen relative z-50 bg-brand-950">
              <SubscriptionModal
                user={effectiveUser}
                onShowTerms={() => setCurrentView(AppView.TERMS)}
              />
            </div>
          )}

          {/* MAIN APP CONTENT */}
          {!ageGateVisible && effectiveUser && effectiveUser.isSubscribed && (
            <div className="flex h-screen overflow-hidden">
              <Nav
                currentView={currentView}
                setView={setCurrentView}
                user={effectiveUser}
                onLogout={handleLogout}
                onNotificationClick={() => setShowNotifications(!showNotifications)}
                showNotifications={showNotifications}
              />

              {/* Offline Indicator */}
              <OfflineIndicator
                isOnline={offlineSync.isOnline}
                pendingCount={offlineSync.pendingCount}
                isSyncing={offlineSync.isSyncing}
                onSyncClick={offlineSync.syncPendingOperations}
              />

              <main className="flex-1 h-full overflow-y-auto relative bg-brand-950 md:ml-64 pb-20 md:pb-0 pt-safe">
                {/* Decorative Background Elements */}
                <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl pointer-events-none mix-blend-multiply"></div>
                <div className="fixed bottom-0 left-64 w-[600px] h-[600px] bg-brand-200/20 rounded-full blur-3xl pointer-events-none mix-blend-multiply"></div>

                <div className="relative z-10 h-full flex flex-col">
                  <div className="flex-1">
                    {renderContent()}
                  </div>

                  <footer className="py-6 px-8 text-center border-t border-brand-800/30 mt-auto">
                    <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-xs text-brand-500">
                      <span>&copy; {new Date().getFullYear()} Jamsoft Inc. All rights reserved.</span>
                      <span className="hidden md:inline text-brand-700">•</span>
                      <a
                        href="https://jamsoftinc.com/privacy-policy-aficionadocigarjournal"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-gold-500 transition-colors underline decoration-brand-700 underline-offset-2"
                      >
                        Privacy Policy
                      </a>
                      <span className="hidden md:inline text-brand-700">•</span>
                      <button
                        onClick={() => setCurrentView(AppView.TERMS)}
                        className="hover:text-gold-500 transition-colors underline decoration-brand-700 underline-offset-2"
                      >
                        Terms of Use
                      </button>
                      <span className="hidden md:inline text-brand-700">•</span>
                      <a
                        href="mailto:support@jamsoftinc.com"
                        className="hover:text-gold-500 transition-colors underline decoration-brand-700 underline-offset-2"
                      >
                        Contact Us
                      </a>
                    </div>
                  </footer>
                </div>
              </main>
            </div>
          )}
        </div>
      </NotificationProvider>
    </ErrorBoundary>
  );
};

// Inner wrapper that has access to user for NotificationProvider
const AppWithNotifications: React.FC = () => {
  return <AppContent />;
};

// Main App wrapper with all providers
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <ToastProvider>
          <AppWithNotifications />
          <InstallPrompt />
        </ToastProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default App;
